import {
  parseInput,
  createTransaction,
  listCategories,
  getMe,
  listAccounts,
  type ParsedLog,
  type CategoryListItem,
  type AccountListItem,
} from '../api-client';
import { renderReviewCard } from './review-card';
import { showCategoryPicker } from './category-picker';
import { renderAccountPicker } from './account-picker';
import { showToast } from '../components/toast';

export interface LogInputCallbacks {
  onTransactionCreated(): void;
}

export function renderLogInput(root: HTMLElement, cb: LogInputCallbacks) {
  root.innerHTML = `
    <form id="log-form" class="log-form">
      <input id="log-text" class="log-text" type="text" placeholder='e.g. "380 spinneys" or "£45 côte"' autocomplete="off" enterkeyhint="send" />
      <div id="log-status" class="muted log-status"></div>
    </form>
    <div id="log-review"></div>
    <div id="picker-mount"></div>
  `;

  const form = root.querySelector<HTMLFormElement>('#log-form')!;
  const input = root.querySelector<HTMLInputElement>('#log-text')!;
  const status = root.querySelector<HTMLElement>('#log-status')!;
  const review = root.querySelector<HTMLElement>('#log-review')!;
  const pickerMount = root.querySelector<HTMLElement>('#picker-mount')!;

  let categories: CategoryListItem[] = [];
  let accounts: AccountListItem[] = [];
  let selectedAccountId = '';
  let currentParsed: ParsedLog | null = null;
  let guardActive = false;

  void (async () => {
    try {
      const [catResult, me, accsResult] = await Promise.all([
        listCategories(),
        getMe(),
        listAccounts(),
      ]);
      categories = catResult.categories;
      accounts = accsResult.accounts;
      const defaultAccountId = me.default_account_id;

      if (!defaultAccountId) {
        guardActive = true;
        form.innerHTML = `
          <div class="log-guard muted" style="padding:16px; text-align:center;">
            <p>Pick your default card before logging.</p>
            <a class="primary" href="/settings">Go to Settings →</a>
          </div>
        `;
        return;
      }

      selectedAccountId = defaultAccountId;
      setTimeout(() => input.focus(), 0);
    } catch {
      /* swallow — review card has a fallback */
    }
  })();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (guardActive) return;
    const text = input.value.trim();
    if (!text) return;
    status.textContent = 'Parsing…';
    try {
      const { parsed } = await parseInput(text);
      status.textContent = '';
      renderReview(review, parsed, text);
    } catch (err) {
      status.textContent = (err as Error).message;
    }
  });

  function renderReview(host: HTMLElement, parsed: ParsedLog & { source?: string }, sourceText: string) {
    currentParsed = parsed;
    renderReviewCard(
      host,
      parsed,
      {
        async onAccept(edited, fromAccountId) {
          const acceptBtn = host.querySelector<HTMLButtonElement>('#review-accept');
          if (acceptBtn?.disabled) return;
          if (acceptBtn) acceptBtn.disabled = true;
          status.textContent = 'Saving…';
          try {
            const result = await createTransaction(
              { ...edited, confidence: 1.0 },
              { fromAccountId },
            );
            input.value = '';
            host.innerHTML = '';
            status.textContent = '';
            // Refresh feed first (non-awaited; runs concurrently with toast)
            cb.onTransactionCreated();
            const accountName =
              accounts.find((a) => a.id === result.fromAccountId)?.name ?? 'account';
            showToast(`Logged to ${accountName}`);
          } catch (err) {
            if (acceptBtn) acceptBtn.disabled = false;
            status.textContent = (err as Error).message;
          }
        },
        onCancel() {
          host.innerHTML = '';
          input.value = sourceText;
          input.focus();
          input.setSelectionRange(sourceText.length, sourceText.length);
        },
        onEditCategory(current) {
          showCategoryPicker((categoryId, subcategoryId) => {
            renderReview(host, { ...current, categoryId, subcategoryId }, sourceText);
          });
        },
        onEditAccount(_cur) {
          renderAccountPicker(pickerMount, accounts, {
            onPick(id) {
              selectedAccountId = id;
              pickerMount.innerHTML = '';
              renderReview(host, currentParsed ?? parsed, sourceText);
            },
            onCancel() {
              pickerMount.innerHTML = '';
            },
          }, { filter: 'liability_credit', selectedId: selectedAccountId });
        },
      },
      categories,
      accounts,
      selectedAccountId,
    );
  }
}
